/**
 * Enterprise Agenda — utilidades HTTP compartidas (P12 Post-Gate-A)
 *
 * Todos los servicios de agenda usan la instancia axios CANÓNICA
 * (@/services/api-client.ts) directamente. El interceptor canónico ya
 * desencapsula los sobres { success, data }, por lo que `response.data`
 * ES el payload del backend.
 *
 * NOTA dual-module: el import lleva extensión `.ts` explícita porque el
 * especificador sin extensión (@/services/api-client) resuelve al shim
 * fetch deprecado (.js) en Vite. Ver src/tests/api-client-canonical.test.ts.
 */

import type { AxiosResponse } from 'axios'

/** Devuelve el payload ya desencapsulado por el interceptor canónico. */
export async function unwrap<T>(p: Promise<AxiosResponse<T>>): Promise<T> {
  return (await p).data
}

/**
 * P13-V3 · texto visible de un error de la API de agenda. El backend responde `{ error, status, code }`
 * (p. ej. 422 REMINDERS_VIA_REMINDERS_RESOURCE, TIMEZONE_NOT_SUPPORTED, REASON_REQUIRED, NOT_EDITABLE),
 * pero axios solo pone "Request failed with status code 422" en `message`: se prefiere el texto del
 * backend para que el usuario vea POR QUÉ se rechazó, con `message` y `fallback` como respaldo.
 */
export function agendaErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>
    const text = typeof body.error === 'string' ? body.error : body.message
    if (typeof text === 'string' && text.trim()) return text
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

/**
 * Normaliza payloads de listado: acepta tanto un array directo como un
 * objeto contenedor (p. ej. { items: [...] } o { delegations: [...] }).
 */
export function pickArray<T>(payload: unknown, ...containerKeys: string[]): T[] {
  if (Array.isArray(payload)) {
    return payload as T[]
  }
  if (payload && typeof payload === 'object') {
    for (const key of containerKeys) {
      const candidate = (payload as Record<string, unknown>)[key]
      if (Array.isArray(candidate)) {
        return candidate as T[]
      }
    }
  }
  return []
}

/**
 * Serializa parámetros de query convirtiendo arrays a listas separadas por
 * coma (convención del contrato §5: ?userIds=1,2&types=MEETING,...).
 * Omite valores null/undefined.
 */
export function serializeParams(
  params: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      if (value.length === 0) continue
      out[key] = value.join(',')
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      out[key] = value
    } else {
      out[key] = String(value)
    }
  }
  return out
}
